/**
 * Recurrencia: cuándo toca el próximo cobro.
 *
 * Es una pieza que **nunca llegó a funcionar** en las aplicaciones anteriores:
 * guardaban las reglas de gastos fijos pero nada las ejecutaba, así que el
 * arriendo nunca se registraba solo. Y en Cuentify la fecha del próximo cobro
 * estaba escrita a mano (`2026-11-…`), de modo que en 2027 todos los gastos
 * recurrentes nuevos nacían con fecha pasada.
 *
 * Aquí se calcula siempre, con cuidado de los casos que rompen las cuentas
 * fáciles: un cobro el día 31 en un mes de 30 días, o el 29 de febrero.
 *
 * Todo se calcula sobre fechas locales de Colombia (`AAAA-MM-DD`) con aritmética
 * de calendario pura, sin objetos `Date` intermedios: así no hay forma de que se
 * cuele un desfase de zona horaria.
 */
/** Etiqueta legible de cada frecuencia. */
export const FREQUENCY_LABELS = {
    WEEKLY: 'Semanal',
    BIWEEKLY: 'Quincenal',
    MONTHLY: 'Mensual',
    BIMONTHLY: 'Bimestral',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    YEARLY: 'Anual',
};
/** Cada cuántos meses se repite (0 = la frecuencia se cuenta en días). */
const MONTHS_BY_FREQUENCY = {
    WEEKLY: 0,
    BIWEEKLY: 0,
    MONTHLY: 1,
    BIMONTHLY: 2,
    QUARTERLY: 3,
    SEMIANNUAL: 6,
    YEARLY: 12,
};
/** Cada cuántos días se repite (solo para las frecuencias por días). */
const DAYS_BY_FREQUENCY = {
    WEEKLY: 7,
    BIWEEKLY: 14,
    MONTHLY: 0,
    BIMONTHLY: 0,
    QUARTERLY: 0,
    SEMIANNUAL: 0,
    YEARLY: 0,
};
/** Días medios de un mes, para estimar por dónde empezar a buscar. */
const AVERAGE_DAYS_PER_MONTH = 30.44;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseISO(dateISO) {
    const match = ISO_RE.exec(dateISO);
    if (!match)
        throw new Error(`Fecha inválida: «${dateISO}». Se espera AAAA-MM-DD.`);
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
function toISO(date) {
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${date.year}-${month}-${day}`;
}
/** Último día del mes (mes 1-12). */
export function lastDayOfMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * Día del mes ajustado: si la regla dice 31 y el mes tiene 30, se usa el 30.
 * Es lo que evita que un cobro «desaparezca» en los meses cortos.
 */
function clampDay(year, month, day) {
    return Math.min(day, lastDayOfMonth(year, month));
}
/** Suma días a una fecha local. */
export function addDaysISO(dateISO, days) {
    const { year, month, day } = parseISO(dateISO);
    const base = new Date(Date.UTC(year, month - 1, day));
    base.setUTCDate(base.getUTCDate() + days);
    return toISO({
        year: base.getUTCFullYear(),
        month: base.getUTCMonth() + 1,
        day: base.getUTCDate(),
    });
}
/** Días de diferencia entre dos fechas locales (positivo si la segunda es posterior). */
export function daysBetweenISO(fromISO, toISOdate) {
    const from = parseISO(fromISO);
    const to = parseISO(toISOdate);
    const a = Date.UTC(from.year, from.month - 1, from.day);
    const b = Date.UTC(to.year, to.month - 1, to.day);
    return Math.round((b - a) / 86_400_000);
}
/** Desplaza un año y mes en meses. */
function shiftMonth(year, month, deltaMonths) {
    const zeroBased = month - 1 + deltaMonths;
    return {
        year: year + Math.floor(zeroBased / 12),
        month: (((zeroBased % 12) + 12) % 12) + 1,
    };
}
/** El día del mes que sigue la regla (el suyo, o el del inicio si no indica). */
function ruleDayOf(rule) {
    if (rule.day && rule.day > 0)
        return rule.day;
    return parseISO(rule.startDate).day;
}
/**
 * La n-ésima ocurrencia, contando desde cero.
 *
 * En las frecuencias mensuales el día **se recalcula en cada mes desde la
 * regla**, así que un cobro el 31 vuelve al 31 en cuanto el mes lo permite (en
 * febrero cae el 28 o el 29, pero en marzo vuelve al 31).
 */
export function nthOccurrence(rule, n) {
    const months = MONTHS_BY_FREQUENCY[rule.frequency];
    if (months === 0) {
        return addDaysISO(rule.startDate, n * DAYS_BY_FREQUENCY[rule.frequency]);
    }
    const base = parseISO(rule.startDate);
    const shifted = shiftMonth(base.year, base.month, n * months);
    return toISO({
        year: shifted.year,
        month: shifted.month,
        day: clampDay(shifted.year, shifted.month, ruleDayOf(rule)),
    });
}
/**
 * La primera ocurrencia que cae en el día de inicio o después.
 *
 * Si la regla dice «día 15» y el inicio es el 20, la primera no es el 15 de ese
 * mismo mes (ya pasó), sino el 15 del mes siguiente.
 */
function firstIndex(rule) {
    const zero = nthOccurrence(rule, 0);
    return daysBetweenISO(zero, rule.startDate) > 0 ? 1 : 0;
}
/**
 * Primer cobro a partir del inicio de la regla.
 */
export function firstOccurrence(rule) {
    return nthOccurrence(rule, firstIndex(rule));
}
/** Ocurrencias dentro de un rango `[desde, hasta]`, ambos inclusive. */
export function occurrencesBetween(rule, fromISO, toISOdate) {
    if (rule.isPaused)
        return [];
    if (daysBetweenISO(fromISO, toISOdate) < 0)
        return [];
    const months = MONTHS_BY_FREQUENCY[rule.frequency];
    const step = DAYS_BY_FREQUENCY[rule.frequency];
    const startIndex = firstIndex(rule);
    const end = rule.endDate ?? null;
    // Se estima por dónde empezar para no recorrer años de ocurrencias.
    const elapsed = daysBetweenISO(rule.startDate, fromISO);
    const perStep = months === 0 ? step : months * AVERAGE_DAYS_PER_MONTH;
    const estimate = elapsed > 0 ? Math.max(0, Math.floor(elapsed / perStep) - 1) : 0;
    let n = Math.max(startIndex, estimate);
    const result = [];
    let guard = 0;
    while (guard < 5000) {
        guard += 1;
        const dateISO = nthOccurrence(rule, n);
        n += 1;
        // Si la regla terminó, o ya se pasó del rango, se acaba.
        // `daysBetweenISO(a, b)` es positivo cuando `b` es posterior a `a`.
        if (end && daysBetweenISO(dateISO, end) < 0)
            break;
        if (daysBetweenISO(dateISO, toISOdate) < 0)
            break;
        if (n - 1 >= startIndex && daysBetweenISO(fromISO, dateISO) >= 0) {
            result.push(dateISO);
        }
    }
    return result;
}
/**
 * Próximo cobro a partir de una fecha. Por defecto, si hoy es el día del cobro,
 * el «próximo» es el del período siguiente.
 */
export function nextOccurrence(rule, fromISO, options = {}) {
    if (rule.isPaused)
        return null;
    const searchFrom = options.includeToday ? fromISO : addDaysISO(fromISO, 1);
    // Holgura suficiente para cubrir el salto más largo (anual).
    const searchTo = addDaysISO(searchFrom, 400);
    return occurrencesBetween(rule, searchFrom, searchTo)[0] ?? null;
}
/** ¿Cuántos días faltan para el próximo cobro? `null` si no hay próximo. */
export function daysUntilNext(rule, todayISOdate) {
    const next = nextOccurrence(rule, todayISOdate);
    return next === null ? null : daysBetweenISO(todayISOdate, next);
}
/**
 * Cobros que ya deberían haberse registrado y no lo están.
 *
 * Es lo que alimenta el motor que ejecuta los gastos fijos: dada la última vez
 * que se ejecutó la regla, devuelve todo lo pendiente hasta hoy, en orden. Así,
 * si el servidor estuvo apagado tres días, al volver se registran los cobros que
 * faltaban en lugar de perderse.
 */
export function pendingOccurrences(rule, lastRunISO, todayISOdate) {
    if (rule.isPaused)
        return [];
    const from = lastRunISO ? addDaysISO(lastRunISO, 1) : rule.startDate;
    const start = daysBetweenISO(from, rule.startDate) > 0 ? rule.startDate : from;
    if (daysBetweenISO(start, todayISOdate) < 0)
        return [];
    return occurrencesBetween(rule, start, todayISOdate);
}
//# sourceMappingURL=recurrence.js.map